import { MarkerIcon } from "src/app/landing/models/map"
import { BEA_CONFIG } from "./landing/bea_config"
import { GBA_CONFIG } from "./landing/gba_config"
import { BIA_CONFIG } from "./landing/bia_config"
import { DARK_FIBER_CONFIG } from "./landing/darkfiber_config"
import { KIT_CONFIG } from "./landing/kit_config"
import { RETE_PRIMARIA_CONFIG } from "./landing/reteprimaria_config"
import { RETE_SECONDARIA_CONFIG } from "./landing/retesecondaria_config"
import { SANITA_DARK_FIBER_CONFIG } from "./landing/sanitadarkfiber_config"
import { SANITA_CONFIG } from "./landing/sanita_config"
import { SCUOLE_DARK_FIBER_CONFIG } from "./landing/scuoledarkfiber_config"
import { SCUOLE_CONFIG } from "./landing/scuole_config"
import { BTS_CONFIG } from "./landing/bts_config"
import { BTS_DARK_FIBER_CONFIG } from "./landing/btsdarkfiber_config"
import { PUNTI_INTERMEDI_CONFIG } from "./landing/puntiintermedi_config"
import { SOPRALLUOGO_CONFIG } from "./landing/sopralluogo_config"
import { WAVELENGTH_CONFIG } from "./landing/wavelength_config"

export const SLUG_CONVERTER = {
	serviziobea: "bea",
	serviziogba: "gba",
	serviziobia: "bia",
	kit: "kit",
	"nodo-operatore-attivo": "noa",
	"nodo-operatore-attivo-dark-fiber": "noadarkfiber",
	scuole: "scuole",
	"scuole-dark-fiber": "scuoledarkfiber",
	sanita: "sanita",
	"sanita-dark-fiber": "sanitadarkfiber",
	"dark-fiber": "darkfiber",
	"rete-primaria": "reteprimaria",
	"rete-secondaria": "retesecondaria",
	"punti-intermedi": "puntiintermedi",
	sopralluogo: "sopralluogo",
	"servizio-wavelength": "wavelength"
}

export const LANDING_CONFIGURATION = {
	bea: BEA_CONFIG,
	gba: GBA_CONFIG,
	bia: BIA_CONFIG,
	kit: KIT_CONFIG,
	/** new config 11/2022 BTS */
	noa: BTS_CONFIG,
	/** new config 11/2022 BTS */
	noadarkfiber: BTS_DARK_FIBER_CONFIG,
	scuole: SCUOLE_CONFIG,
	scuoledarkfiber: SCUOLE_DARK_FIBER_CONFIG,
	darkfiber: DARK_FIBER_CONFIG,
	reteprimaria: RETE_PRIMARIA_CONFIG,
	retesecondaria: RETE_SECONDARIA_CONFIG,
	sanita: SANITA_CONFIG,
	sanitadarkfiber: SANITA_DARK_FIBER_CONFIG,
	puntiintermedi: PUNTI_INTERMEDI_CONFIG,
	sopralluogo: SOPRALLUOGO_CONFIG,
	wavelength: WAVELENGTH_CONFIG
}

export interface Config {
	data: {
		route: string
		page_title: string
		service_title: string
		service_description: string
		service_name: string
		service_slug: string
		errorTimeout: number
		feasibilityModelSingle: boolean
		feasibilityEnabled: boolean
		api: {
			massiveImport: {
				massiveDetailsPath: string
				massiveUploadPath: string
				massiveDownloadPath: string
				massiveDownloadPath_2p?: string
				massiveP2pSalesforcePath: string
				max_file_rows: number
				info_message?: string
			}
			p2pPath: string
			bboxPath: string
			buildingSearchParams: { [key: string]: number | string } | null
		}
		dynamic_aend_name: string
	}
	multiple_points: {
		/**  if the number of points can change */
		variable: boolean
		/** max number of points */
		max: number
		points: {
			b_end: ConfigPoint
			a_end: ConfigPoint
		}
		/** initial points and their value, with the list of all possible values for each one */
		initial_points: string[]
	}
	map: {
		map_events: string[]
		/** whether picked marker contains a label (a number that represents the input number) */
		marker_has_label: boolean
		/** whether picked marker has the default icon or not. Default: orange marker. Different: usually ico_pink (pink drop) and it is associated with the marker_has_label value */
		marker_default_icon: MarkerIcon
		/** lever at which all markers must be removed from map */
		clear_markers_zoom_level: number
	}
	research_methods: {
		id_building: boolean
		id_bts_noa: boolean
		id_bts_building: boolean
		id_school: boolean
		id_healthcare: boolean
		punti_intermedi: boolean
		pop_pcn_xpop: boolean
	}
	/** to decide which building types to show, i.e.: 'CoveredCD' */
	area_types: string[]
	/** shorter version of the above, i.e.: 'C&D' */
	area_types_short: string[] // TODO to be refactored, unified with the above parameter
	kml: boolean
	pop: {
		load: boolean
		view_initial: boolean
		click: boolean
		loadCities: boolean
	}
	pcn: {
		load: boolean
		view_initial: boolean
		click: boolean
	}
	pop_agf: {
		load: boolean
		view_initial: boolean
		click: boolean
	}
	poi: {
		load: boolean
		view_initial: boolean
		click: boolean
		x_pop: boolean
		is_registered: boolean
		custom_research_method: string
		loadCities: boolean
	}

	pfs: {
		load: boolean
		view_initial: boolean
		click: boolean
		type: string
	}
	roe: {
		load: boolean
		view_initial: boolean
		click: boolean
		type: string
	}
	cno: {
		load: boolean
		view_initial: boolean
		click: boolean
		type: string
	}
	pozzetto: {
		load: boolean
		view_initial: boolean
		click: boolean
		type: string
	}
	building: {
		load: boolean
		click: boolean
		placeholder: string
	}
	noa: {
		load: boolean
		click: boolean
		placeholder: string
	}
	bts: {
		load: boolean
		/** list of elements for BTS (see BTS config) */
		types: any
	}
	punti_intermedi: {
		load: boolean
		/** list of elements for punti intermedi */
		types: any
	}
	pop_pcn_xpop: {
		load: boolean
		/** list of elements for pop, pcn, xpop */
		types: any
	}
	school: {
		load: boolean
		click: boolean
		placeholder: string
	}
	healthcare: {
		load: boolean
		click: boolean
		placeholder: string
	}
}

export interface ConfigPoint {
	default_method: string
	switch_methods: boolean
	address: boolean
	pop: boolean
	poi: boolean
	pfs: boolean
	roe: boolean
	coordinates: boolean
	id_building: boolean
	id_bts_noa: boolean
	id_bts_building: boolean
	id_school: boolean
	city_noa: boolean
	city_pop: boolean
	city_poi: boolean
	city_building: boolean
	id_healthcare: boolean
	upload: boolean
	/** POP and POI together in the same autocomplete */
	popAndPoiAutocomplete: boolean
	punti_intermedi: boolean
	pop_pcn_xpop: boolean
}
